// @flow
import flatten from 'lodash/fp/flatten';
import reverse from 'lodash/fp/reverse';

type ChildrenGetter<N> = (N) => $ReadOnlyArray<N>;

type WithChildren<N> = (subject: N, newChildren: $ReadOnlyArray<N>) => N;

type Mapper<N, M> = (
  subject: N,
  ancestors: $ReadOnlyArray<N>,
  newChildren: $ReadOnlyArray<M>,
) => M;

export const nodes = <N>(
  childrenGetter: ChildrenGetter<N>,
  root: N,
): $ReadOnlyArray<N> => [
  root,
  ...flatten(childrenGetter(root).map((n) => nodes(childrenGetter, n))),
];

export const leavesInNodes = <N>(
  childrenGetter: ChildrenGetter<N>,
  root: N,
): $ReadOnlyArray<N> => reverse(nodes(childrenGetter, root));

const isLeaf = <N>(childrenGetter: ChildrenGetter<N>, node: N): boolean =>
  childrenGetter(node).length === 0;

export const leaves = <N>(
  childrenGetter: ChildrenGetter<N>,
  root: N,
): $ReadOnlyArray<N> =>
  nodes(childrenGetter, root).filter((n) => isLeaf(childrenGetter, n));

// eslint-disable-next-line arrow-parens
export const rootOutPaths = <N>(
  childrenGetter: ChildrenGetter<N>,
  root: N,
): $ReadOnlyArray<$ReadOnlyArray<N>> => {
  const children = childrenGetter(root);
  if (children.length === 0) return [[root]];

  return flatten(children.map((n) => rootOutPaths(childrenGetter, n))).map(
    (path) => [root, ...path],
  );
};

export const leavesInPaths = <N>(
  childrenGetter: ChildrenGetter<N>,
  root: N,
): Array<$ReadOnlyArray<N>> => rootOutPaths(childrenGetter, root).map(reverse);

// eslint-disable-next-line arrow-parens
export const map = <N, M>(
  childrenGetter: ChildrenGetter<N>,
  withChildren: WithChildren<M>,
  mapper: Mapper<N, M>,
  root: N,
  ancestors: $ReadOnlyArray<N> = [],
): M => {
  const newChildren = childrenGetter(root).map((n) =>
    map(childrenGetter, withChildren, mapper, n, [...ancestors, root]),
  );

  return withChildren(mapper(root, ancestors, newChildren), newChildren);
};
